import React, { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, formatApiErrorDetail } from "@/lib/api";
import { REVIEW } from "@/constants/appIds";
import { formatRupiah, parseRupiah, formatDateTimeJakarta, STATUS_META, OCR_META } from "@/lib/format";
import { ReceiptImage } from "@/components/ReceiptImage";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import {
  ZoomIn, ZoomOut, RotateCw, Maximize, Sun, Plus, Trash2, Save, X, Check,
  AlertTriangle, Loader2, RefreshCw, Copy, ArrowLeft, History as HistoryIcon,
} from "lucide-react";

const emptyItem = () => ({ name: "", qty: null, unit: "", unit_price: null, amount: null, needs_review: false });

export default function ReviewDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const qc = useQueryClient();

  const { data: receipt, isLoading } = useQuery({
    queryKey: ["receipt", id],
    queryFn: async () => (await api.get(`/receipts/${id}`)).data,
    refetchInterval: (q) => (["queued", "processing"].includes(q.state.data?.ocr_status) ? 4000 : false),
  });
  const { data: outlets = [] } = useQuery({ queryKey: ["outlets"], queryFn: async () => (await api.get("/outlets")).data });
  const { data: categories = [] } = useQuery({ queryKey: ["categories"], queryFn: async () => (await api.get("/categories")).data });
  const { data: options } = useQuery({ queryKey: ["options"], queryFn: async () => (await api.get("/options")).data });
  const { data: masterItems = [] } = useQuery({ queryKey: ["master-items"], queryFn: async () => (await api.get("/master-items")).data });
  const { data: duplicates = [] } = useQuery({
    queryKey: ["dups", id],
    queryFn: async () => (await api.get(`/receipts/${id}/duplicates`)).data,
    enabled: !!receipt,
  });

  const [form, setForm] = useState(null);
  const [meta, setMeta] = useState({ outlet_id: "", category: "", payment_method: "", fund_source: "" });
  const [zoom, setZoom] = useState(1);
  const [rotate, setRotate] = useState(0);
  const [bright, setBright] = useState(100);
  const [photoIdx, setPhotoIdx] = useState(0);
  const [saving, setSaving] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [approveOpen, setApproveOpen] = useState(false);
  const [varianceNote, setVarianceNote] = useState("");
  const [varianceConfirmed, setVarianceConfirmed] = useState(false);

  useEffect(() => {
    if (receipt && form === null) {
      const c = receipt.corrected || { items: [] };
      setForm({
        supplier: c.supplier || "", receipt_number: c.receipt_number || "",
        transaction_date: c.transaction_date || "", items: (c.items || []).map((i) => ({ ...i })),
        discount: c.discount ?? null, tax: c.tax ?? null, additional_fee: c.additional_fee ?? null,
        grand_total: c.grand_total ?? null, notes: c.notes || "", unreadable_fields: c.unreadable_fields || [],
      });
      setMeta({
        outlet_id: receipt.outlet_id || "", category: receipt.category || "",
        payment_method: receipt.payment_method || "", fund_source: receipt.fund_source || "",
      });
    }
  }, [receipt, form]);

  const totals = useMemo(() => {
    if (!form) return null;
    let subtotal = 0;
    form.items.forEach((it) => {
      let amt = it.amount;
      if ((amt === null || amt === undefined || amt === "") && it.qty != null && it.unit_price != null)
        amt = Number(it.qty) * Number(it.unit_price);
      subtotal += Number(amt) || 0;
    });
    const computed = subtotal - (Number(form.discount) || 0) + (Number(form.tax) || 0) + (Number(form.additional_fee) || 0);
    const receiptTotal = form.grand_total != null && form.grand_total !== "" ? Number(form.grand_total) : null;
    const variance = receiptTotal === null ? null : computed - receiptTotal;
    return { subtotal, computed, receiptTotal, variance };
  }, [form]);

  if (isLoading || !receipt || !form)
    return <div className="flex items-center justify-center h-64"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>;

  const readonly = false;
  const setItem = (i, key, val) => {
    setForm((f) => {
      const items = [...f.items];
      items[i] = { ...items[i], [key]: val };
      return { ...f, items };
    });
  };

  const handleName = (i, val) => {
    setForm((f) => {
      const items = [...f.items];
      const cur = { ...items[i], name: val };
      const match = masterItems.find((m) => m.name.toLowerCase() === val.trim().toLowerCase());
      if (match && (!cur.unit || cur.unit === "")) cur.unit = match.unit || cur.unit;
      items[i] = cur;
      return { ...f, items };
    });
  };

  const matchFor = (name) => masterItems.find((m) => m.name.toLowerCase() === (name || "").trim().toLowerCase());

  const buildCorrected = () => ({
    supplier: form.supplier || null, receipt_number: form.receipt_number || null,
    transaction_date: form.transaction_date || null,
    items: form.items.map((it) => ({
      name: it.name || "", qty: it.qty === "" ? null : it.qty, unit: it.unit || null,
      unit_price: it.unit_price === "" ? null : it.unit_price,
      amount: it.amount === "" ? null : it.amount, needs_review: !!it.needs_review,
    })),
    discount: form.discount === "" ? null : form.discount,
    tax: form.tax === "" ? null : form.tax,
    additional_fee: form.additional_fee === "" ? null : form.additional_fee,
    grand_total: form.grand_total === "" ? null : form.grand_total,
    notes: form.notes || "", unreadable_fields: form.unreadable_fields || [],
  });

  const saveDraft = async () => {
    setSaving(true);
    try {
      await api.put(`/receipts/${id}`, { corrected: buildCorrected(), ...meta });
      toast.success("Draf tersimpan.");
      qc.invalidateQueries({ queryKey: ["receipt", id] });
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const doReject = async () => {
    if (!rejectReason.trim()) { toast.error("Isi alasan penolakan."); return; }
    try {
      await api.post(`/receipts/${id}/reject`, { reason: rejectReason });
      toast.success("Nota ditolak.");
      setRejectOpen(false);
      qc.invalidateQueries({ queryKey: ["receipt", id] });
      nav("/review");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const openApprove = async () => {
    await saveDraft();
    setApproveOpen(true);
  };

  const doApprove = async () => {
    try {
      await api.post(`/receipts/${id}/approve`, {
        idempotency_key: `${id}-${Date.now()}`,
        variance_note: varianceNote, variance_confirmed: varianceConfirmed,
      });
      toast.success("Disetujui & dikirim ke Daily Report.");
      setApproveOpen(false);
      qc.invalidateQueries();
      nav("/review");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const reprocess = async () => {
    try {
      await api.post(`/receipts/${id}/reprocess`);
      toast.success("Antre baca ulang AI. Draf & koreksi tidak diubah.");
      qc.invalidateQueries({ queryKey: ["receipt", id] });
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const hasVariance = totals?.variance !== null && totals?.variance !== 0;

  // ---- Image panel ----
  const ImagePanel = (
    <div className="flex flex-col h-full">
      <div className="flex items-center gap-1 flex-wrap mb-2">
        <Button size="icon" variant="outline" data-testid={REVIEW.zoomIn} onClick={() => setZoom((z) => Math.min(z + 0.25, 4))}><ZoomIn className="w-4 h-4" /></Button>
        <Button size="icon" variant="outline" data-testid={REVIEW.zoomOut} onClick={() => setZoom((z) => Math.max(z - 0.25, 0.5))}><ZoomOut className="w-4 h-4" /></Button>
        <Button size="icon" variant="outline" data-testid={REVIEW.rotate} onClick={() => setRotate((r) => (r + 90) % 360)}><RotateCw className="w-4 h-4" /></Button>
        <Button size="icon" variant="outline" data-testid={REVIEW.brightness} onClick={() => setBright((b) => (b >= 160 ? 100 : b + 30))}><Sun className="w-4 h-4" /></Button>
        <Button size="icon" variant="outline" data-testid={REVIEW.reset} onClick={() => { setZoom(1); setRotate(0); setBright(100); }}><Maximize className="w-4 h-4" /></Button>
        <span className="text-xs text-muted-foreground ml-1">{Math.round(zoom * 100)}%</span>
      </div>
      <div className="flex-1 overflow-auto nh-scroll bg-neutral-900 rounded-lg flex items-center justify-center min-h-[300px] lg:min-h-[500px] p-4">
        {receipt.photos?.[photoIdx] && (
          <ReceiptImage path={receipt.photos[photoIdx].storage_path} zoom={zoom} rotate={rotate} brightness={bright}
            className="max-w-full object-contain origin-center" />
        )}
      </div>
      {receipt.photos?.length > 1 && (
        <div className="flex gap-2 mt-2 overflow-x-auto">
          {receipt.photos.map((p, i) => (
            <button key={i} onClick={() => setPhotoIdx(i)}
              className={`text-xs px-2 py-1 rounded border ${i === photoIdx ? "bg-primary text-primary-foreground border-primary" : "border-border"}`}>
              Foto {i + 1}
            </button>
          ))}
        </div>
      )}
    </div>
  );

  // ---- Editable table panel ----
  const EditPanel = (
    <div className="space-y-4">
      {receipt.ocr_status === "processing" || receipt.ocr_status === "queued" ? (
        <div className="flex items-center gap-2 text-sm text-blue-700 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
          <Loader2 className="w-4 h-4 animate-spin" /> AI sedang membaca nota… halaman akan diperbarui otomatis.
        </div>
      ) : null}
      {(receipt.ocr_status === "failed" || receipt.ocr_status === "limited") && (
        <div className="flex items-center justify-between gap-2 text-sm text-orange-800 bg-orange-50 border border-orange-200 rounded-lg px-3 py-2">
          <span>{receipt.ocr_status === "limited" ? "Layanan AI penuh/limit. Data tersimpan — isi manual atau coba lagi." : "AI gagal membaca. Isi manual atau coba lagi."}</span>
          <Button size="sm" variant="outline" data-testid={REVIEW.reprocess} onClick={reprocess}><RefreshCw className="w-3 h-3 mr-1" /> Coba lagi</Button>
        </div>
      )}
      {form.unreadable_fields?.length > 0 && (
        <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
          Perlu diperiksa: {form.unreadable_fields.join(", ")}
        </div>
      )}

      <div className="grid grid-cols-2 gap-3">
        <div><Label className="text-xs">Supplier</Label><Input data-testid="field-supplier" value={form.supplier} onChange={(e) => setForm({ ...form, supplier: e.target.value })} className="mt-1 h-9" /></div>
        <div><Label className="text-xs">No. Nota</Label><Input data-testid="field-receipt-number" value={form.receipt_number} onChange={(e) => setForm({ ...form, receipt_number: e.target.value })} className="mt-1 h-9" /></div>
        <div><Label className="text-xs">Tanggal Transaksi</Label><Input type="date" data-testid="field-transaction-date" value={form.transaction_date || ""} onChange={(e) => setForm({ ...form, transaction_date: e.target.value })} className="mt-1 h-9" /></div>
        <div className="text-xs text-muted-foreground flex items-end pb-1">Waktu unggah: {formatDateTimeJakarta(receipt.uploaded_at)}</div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs">Outlet</Label>
          <Select value={meta.outlet_id} onValueChange={(v) => setMeta({ ...meta, outlet_id: v })}>
            <SelectTrigger className="mt-1 h-9" data-testid="field-outlet"><SelectValue placeholder="Pilih" /></SelectTrigger>
            <SelectContent>{outlets.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Kategori Pengeluaran</Label>
          <Select value={meta.category || ""} onValueChange={(v) => setMeta({ ...meta, category: v })}>
            <SelectTrigger className="mt-1 h-9" data-testid="field-category"><SelectValue placeholder="Pilih" /></SelectTrigger>
            <SelectContent>{categories.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Metode Pembayaran</Label>
          <Select value={meta.payment_method || ""} onValueChange={(v) => setMeta({ ...meta, payment_method: v })}>
            <SelectTrigger className="mt-1 h-9" data-testid="field-payment"><SelectValue placeholder="Pilih" /></SelectTrigger>
            <SelectContent>{options?.payment_methods?.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Sumber Dana</Label>
          <Select value={meta.fund_source || ""} onValueChange={(v) => setMeta({ ...meta, fund_source: v })}>
            <SelectTrigger className="mt-1 h-9" data-testid="field-fund"><SelectValue placeholder="Pilih" /></SelectTrigger>
            <SelectContent>{options?.fund_sources?.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>

      {/* Items */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <Label className="text-xs uppercase tracking-wide text-muted-foreground">Barang ({form.items.length})</Label>
          <Button size="sm" variant="outline" data-testid={REVIEW.addItem} onClick={() => setForm({ ...form, items: [...form.items, emptyItem()] })}>
            <Plus className="w-3 h-3 mr-1" /> Tambah
          </Button>
        </div>
        <div className="space-y-2">
          {form.items.map((it, i) => (
            <div key={i} data-testid={`item-row-${i}`} className={`rounded-lg border p-2 ${it.needs_review ? "border-amber-300 bg-amber-50/40" : "border-border"}`}>
              <div className="flex gap-2 items-center">
                <Input list="master-items-list" placeholder="Nama barang" data-testid={`item-name-${i}`} value={it.name || ""} onChange={(e) => handleName(i, e.target.value)} className="h-9 flex-1" />
                <Button size="icon" variant="ghost" onClick={() => setForm({ ...form, items: form.items.filter((_, idx) => idx !== i) })}><Trash2 className="w-4 h-4 text-muted-foreground" /></Button>
              </div>
              {matchFor(it.name) && (
                <p className="text-[11px] text-emerald-600 mt-1">✓ Cocok master: {matchFor(it.name).name}{matchFor(it.name).unit ? ` · satuan ${matchFor(it.name).unit}` : ""}</p>
              )}
              <div className="grid grid-cols-4 gap-2 mt-2">
                <div><span className="text-[10px] text-muted-foreground">Qty</span><Input type="number" data-testid={`item-qty-${i}`} value={it.qty ?? ""} onChange={(e) => setItem(i, "qty", e.target.value === "" ? "" : Number(e.target.value))} className="h-8" /></div>
                <div><span className="text-[10px] text-muted-foreground">Satuan</span><Input data-testid={`item-unit-${i}`} value={it.unit ?? ""} onChange={(e) => setItem(i, "unit", e.target.value)} className="h-8" /></div>
                <div><span className="text-[10px] text-muted-foreground">Harga Satuan</span><Input type="number" data-testid={`item-price-${i}`} value={it.unit_price ?? ""} onChange={(e) => setItem(i, "unit_price", e.target.value === "" ? "" : Number(e.target.value))} className="h-8" /></div>
                <div><span className="text-[10px] text-muted-foreground">Jumlah</span><Input type="number" data-testid={`item-amount-${i}`} value={it.amount ?? ""} onChange={(e) => setItem(i, "amount", e.target.value === "" ? "" : Number(e.target.value))} className="h-8" /></div>
              </div>
              <label className="flex items-center gap-2 mt-2 text-[11px] text-muted-foreground">
                <Checkbox checked={!!it.needs_review} onCheckedChange={(v) => setItem(i, "needs_review", !!v)} /> Tandai "perlu diperiksa"
              </label>
            </div>
          ))}
          <datalist id="master-items-list">
            {masterItems.map((m) => <option key={m.id} value={m.name}>{m.unit ? `${m.name} (${m.unit})` : m.name}</option>)}
          </datalist>
          {form.items.length === 0 && <p className="text-sm text-muted-foreground text-center py-3">Belum ada barang. Tambah manual jika AI tidak membaca.</p>}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div><Label className="text-xs">Diskon</Label><Input type="number" data-testid="field-discount" value={form.discount ?? ""} onChange={(e) => setForm({ ...form, discount: e.target.value === "" ? "" : Number(e.target.value) })} className="mt-1 h-9" /></div>
        <div><Label className="text-xs">Pajak</Label><Input type="number" data-testid="field-tax" value={form.tax ?? ""} onChange={(e) => setForm({ ...form, tax: e.target.value === "" ? "" : Number(e.target.value) })} className="mt-1 h-9" /></div>
        <div><Label className="text-xs">Biaya Tambahan</Label><Input type="number" data-testid="field-fee" value={form.additional_fee ?? ""} onChange={(e) => setForm({ ...form, additional_fee: e.target.value === "" ? "" : Number(e.target.value) })} className="mt-1 h-9" /></div>
        <div><Label className="text-xs">Total di Nota</Label><Input type="number" data-testid="field-grand-total" value={form.grand_total ?? ""} onChange={(e) => setForm({ ...form, grand_total: e.target.value === "" ? "" : Number(e.target.value) })} className="mt-1 h-9" /></div>
      </div>

      {/* Totals + variance */}
      <Card className="p-3 bg-secondary/50">
        <div className="flex justify-between text-sm"><span className="text-muted-foreground">Subtotal barang</span><span className="font-mono">{formatRupiah(totals.subtotal)}</span></div>
        <div className="flex justify-between text-sm mt-1"><span className="text-muted-foreground">Total (hitung sistem)</span><span className="font-mono font-bold">{formatRupiah(totals.computed)}</span></div>
        <div className="flex justify-between text-sm mt-1"><span className="text-muted-foreground">Total di nota</span><span className="font-mono">{totals.receiptTotal === null ? "—" : formatRupiah(totals.receiptTotal)}</span></div>
        {hasVariance && (
          <div className="flex items-center gap-2 mt-2 text-sm text-orange-700 bg-orange-100 border border-orange-300 rounded px-2 py-1.5">
            <AlertTriangle className="w-4 h-4" /> Selisih {formatRupiah(Math.abs(totals.variance))} — wajib diselesaikan atau diberi penjelasan.
          </div>
        )}
        {!hasVariance && totals.receiptTotal !== null && (
          <div className="flex items-center gap-2 mt-2 text-sm text-emerald-700"><Check className="w-4 h-4" /> Total cocok dengan nota.</div>
        )}
      </Card>
    </div>
  );

  return (
    <div className="space-y-4 nh-fade">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => nav("/review")}><ArrowLeft className="w-5 h-5" /></Button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="text-xl font-bold tracking-tight truncate">Pemeriksaan Nota</h1>
            <Badge variant="outline" className={STATUS_META[receipt.status]?.cls}>{STATUS_META[receipt.status]?.label}</Badge>
            <Badge variant="outline" className={OCR_META[receipt.ocr_status]?.cls}>{OCR_META[receipt.ocr_status]?.label}</Badge>
          </div>
          <p className="text-xs text-muted-foreground">Diunggah oleh {receipt.uploaded_by_name} · {formatDateTimeJakarta(receipt.uploaded_at)}</p>
        </div>
      </div>

      {duplicates.length > 0 && (
        <Card className="p-3 border-purple-300 bg-purple-50">
          <div className="flex items-center gap-2 text-sm text-purple-800 font-medium"><Copy className="w-4 h-4" /> Terdeteksi {duplicates.length} dugaan nota duplikat</div>
          <div className="mt-2 space-y-1">
            {duplicates.map((d) => (
              <button key={d.id} onClick={() => nav(`/review/${d.id}`)} className="text-xs text-purple-700 hover:underline block">
                {d.supplier || "Nota"} · {d.outlet_name} · {formatDateTimeJakarta(d.uploaded_at)} · {d.reasons.join(", ")}
              </button>
            ))}
          </div>
        </Card>
      )}

      {/* Desktop split, mobile tabs */}
      <div className="hidden lg:grid grid-cols-2 gap-5">
        <Card className="p-4">{ImagePanel}</Card>
        <Card className="p-4 max-h-[80vh] overflow-auto nh-scroll">{EditPanel}</Card>
      </div>
      <div className="lg:hidden">
        <Tabs defaultValue="edit">
          <TabsList className="w-full grid grid-cols-2">
            <TabsTrigger value="photo">Foto Nota</TabsTrigger>
            <TabsTrigger value="edit">Data & Edit</TabsTrigger>
          </TabsList>
          <TabsContent value="photo"><Card className="p-3">{ImagePanel}</Card></TabsContent>
          <TabsContent value="edit"><Card className="p-3">{EditPanel}</Card></TabsContent>
        </Tabs>
      </div>

      {/* Audit trail */}
      {receipt.audit_log?.length > 0 && (
        <Card className="p-4">
          <div className="flex items-center gap-2 text-sm font-semibold mb-2"><HistoryIcon className="w-4 h-4" /> Jejak Perubahan</div>
          <div className="space-y-1.5">
            {receipt.audit_log.slice().reverse().map((a, i) => (
              <div key={i} className="text-xs text-muted-foreground flex gap-2">
                <span className="font-mono">{formatDateTimeJakarta(a.at)}</span>
                <span className="font-medium text-foreground">{a.by}</span>
                <span>· {a.action}{a.detail ? ` — ${a.detail}` : ""}</span>
              </div>
            ))}
          </div>
          {receipt.status === "approved" && (
            <div className="mt-3 text-xs bg-emerald-50 border border-emerald-200 rounded px-2 py-1.5 text-emerald-800">
              Disetujui oleh {receipt.reviewed_by_name} · Ref Daily Report: {receipt.daily_report_ref} · versi {receipt.approved_version_hash}
            </div>
          )}
        </Card>
      )}

      {/* Sticky action bar */}
      <div className="sticky bottom-0 -mx-4 sm:-mx-6 lg:mx-0 bg-card/95 backdrop-blur border-t border-border p-3 flex flex-wrap gap-2 justify-end z-20">
        <Button variant="outline" onClick={saveDraft} disabled={saving} data-testid={REVIEW.saveDraft}>
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-2" /> Simpan Draf</>}
        </Button>
        <Button variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" onClick={() => setRejectOpen(true)} data-testid={REVIEW.reject}>
          <X className="w-4 h-4 mr-2" /> Tolak Nota
        </Button>
        <Button onClick={openApprove} data-testid={REVIEW.approve} className="rounded-full">
          <Check className="w-4 h-4 mr-2" /> Setujui & Kirim ke Daily Report
        </Button>
      </div>

      {/* Reject dialog */}
      <Dialog open={rejectOpen} onOpenChange={setRejectOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Tolak Nota</DialogTitle>
            <DialogDescription>Beri alasan penolakan. Nota tidak akan masuk Daily Report.</DialogDescription>
          </DialogHeader>
          <Textarea value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} placeholder="Alasan…" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectOpen(false)}>Batal</Button>
            <Button variant="destructive" onClick={doReject} data-testid="confirm-reject-button">Tolak</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Approve dialog */}
      <Dialog open={approveOpen} onOpenChange={setApproveOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Setujui & Kirim ke Daily Report</DialogTitle>
            <DialogDescription>Total dihitung ulang oleh sistem sebelum dikirim.</DialogDescription>
          </DialogHeader>
          <div className="text-sm space-y-1">
            <div className="flex justify-between"><span className="text-muted-foreground">Total sistem</span><span className="font-mono font-bold">{formatRupiah(totals.computed)}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Total nota</span><span className="font-mono">{totals.receiptTotal === null ? "—" : formatRupiah(totals.receiptTotal)}</span></div>
          </div>
          {hasVariance && (
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm text-orange-700 bg-orange-100 border border-orange-300 rounded px-2 py-1.5">
                <AlertTriangle className="w-4 h-4" /> Ada selisih {formatRupiah(Math.abs(totals.variance))}. Beri penjelasan & konfirmasi.
              </div>
              <Textarea data-testid={REVIEW.varianceNote} value={varianceNote} onChange={(e) => setVarianceNote(e.target.value)} placeholder="Penjelasan selisih…" />
              <label className="flex items-center gap-2 text-sm">
                <Checkbox data-testid={REVIEW.varianceConfirm} checked={varianceConfirmed} onCheckedChange={(v) => setVarianceConfirmed(!!v)} />
                Saya konfirmasi selisih ini sudah diperiksa.
              </label>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setApproveOpen(false)}>Batal</Button>
            <Button onClick={doApprove} data-testid="confirm-approve-button" disabled={hasVariance && (!varianceConfirmed || !varianceNote.trim())}>
              <Check className="w-4 h-4 mr-2" /> Setujui
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
